import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { DEFAULT_INTAKE_LIST_STATUS } from "@/constants/intakeFilters";
import { formatDateForUrl } from "@/utils/dateUtils";

/**
 * Pinia store for intake list page filter state.
 *
 * This store maintains filter state that persists during the session,
 * allowing navigation to detail pages and back while preserving filters.
 * Applies to intake documents and orders displayed on the intake list page (IntakeTablePage).
 *
 * The store works alongside URL query parameters:
 * - URL params are the source of truth on page load
 * - Store syncs with URL params and maintains state during navigation
 * - Components use getNavigationQuery() to get params for router.push()
 */
export const useIntakeListFiltersStore = defineStore("intakeListFilters", () => {
  const status = ref(DEFAULT_INTAKE_LIST_STATUS);
  const sortBy = ref(null);
  const search = ref("");
  const dateFrom = ref(null);
  const dateTo = ref(null);
  const dueFrom = ref(null);
  const dueTo = ref(null);
  const documentType = ref([]);
  const modifiedBy = ref([]);
  const sentBy = ref([]);
  const tags = ref("");
  const tagsMatch = ref("all");
  const substatus = ref("");
  const substatusMatch = ref("all");
  const assignedUser = ref("");
  const assignedGroup = ref("");
  const sortKey = ref("");
  const sortDir = ref("desc");
  const page = ref(1);
  const showActionNeeded = ref(true);

  // Computed: check if any non-default filters are active (drives the
  // table view's restore-from-store branch).
  const hasTableFilters = computed(() => {
    return Boolean(
      status.value !== DEFAULT_INTAKE_LIST_STATUS ||
        (search.value && search.value.trim()) ||
        dateFrom.value ||
        dateTo.value ||
        dueFrom.value ||
        dueTo.value ||
        sortBy.value ||
        documentType.value.length ||
        modifiedBy.value.length ||
        sentBy.value.length ||
        tags.value ||
        substatus.value ||
        assignedUser.value ||
        assignedGroup.value ||
        sortKey.value ||
        sortDir.value !== "desc" ||
        page.value > 1 ||
        !showActionNeeded.value
    );
  });

  /**
   * Update all filters at once (typically called when URL params change)
   */
  function setFilters({
    status: newStatus,
    sortBy: newSortBy,
    search: newSearch,
    dateFrom: newDateFrom,
    dateTo: newDateTo,
    dueFrom: newDueFrom,
    dueTo: newDueTo,
    documentType: newDocumentType,
    modifiedBy: newModifiedBy,
    sentBy: newSentBy,
    tags: newTags,
    tagsMatch: newTagsMatch,
    substatus: newSubstatus,
    substatusMatch: newSubstatusMatch,
    assignedUser: newAssignedUser,
    assignedGroup: newAssignedGroup,
    sortKey: newSortKey,
    sortDir: newSortDir,
    page: newPage,
    showActionNeeded: newShowActionNeeded,
  }) {
    if (newStatus !== undefined) {
      status.value = newStatus;
    }
    if (newSortBy !== undefined) {
      sortBy.value = newSortBy;
    }
    if (newSearch !== undefined) {
      search.value = newSearch;
    }
    if (newDateFrom !== undefined) {
      dateFrom.value = newDateFrom;
    }
    if (newDateTo !== undefined) {
      dateTo.value = newDateTo;
    }
    if (newDueFrom !== undefined) {
      dueFrom.value = newDueFrom;
    }
    if (newDueTo !== undefined) {
      dueTo.value = newDueTo;
    }
    if (newDocumentType !== undefined) {
      documentType.value = newDocumentType;
    }
    if (newModifiedBy !== undefined) {
      modifiedBy.value = newModifiedBy;
    }
    if (newSentBy !== undefined) {
      sentBy.value = newSentBy;
    }
    if (newTags !== undefined) {
      tags.value = newTags;
    }
    if (newTagsMatch !== undefined) {
      tagsMatch.value = newTagsMatch;
    }
    if (newSubstatus !== undefined) {
      substatus.value = newSubstatus;
    }
    if (newSubstatusMatch !== undefined) {
      substatusMatch.value = newSubstatusMatch;
    }
    if (newAssignedUser !== undefined) {
      assignedUser.value = newAssignedUser;
    }
    if (newAssignedGroup !== undefined) {
      assignedGroup.value = newAssignedGroup;
    }
    if (newSortKey !== undefined) {
      sortKey.value = newSortKey;
    }
    if (newSortDir !== undefined) {
      sortDir.value = newSortDir;
    }
    if (newPage !== undefined) {
      page.value = newPage;
    }
    if (newShowActionNeeded !== undefined) {
      showActionNeeded.value = newShowActionNeeded;
    }
  }

  /**
   * Get query params object for navigation to intake list page.
   * Only includes non-default values to keep URLs clean.
   * Param names match IntakeTablePage.updateUrl().
   */
  function getNavigationQuery() {
    const query = {};

    if (status.value && status.value !== DEFAULT_INTAKE_LIST_STATUS) {
      query.status = status.value;
    }

    if (sortBy.value) {
      query.sort = sortBy.value;
    }

    if (search.value && search.value.trim()) {
      query.search = search.value.trim();
    }

    const formattedDateFrom = formatDateForUrl(dateFrom.value);
    if (formattedDateFrom) {
      query.dateFrom = formattedDateFrom;
    }

    const formattedDateTo = formatDateForUrl(dateTo.value);
    if (formattedDateTo) {
      query.dateTo = formattedDateTo;
    }

    if (documentType.value.length) {
      query.documentType = [...documentType.value];
    }

    if (modifiedBy.value.length) {
      query.modifiedBy = [...modifiedBy.value];
    }

    if (sentBy.value.length) {
      query.sentBy = [...sentBy.value];
    }

    if (tags.value) {
      query.tags = tags.value;
      if (tagsMatch.value === "any") {
        query.tagsMatch = "any";
      }
      const formattedDueFrom = formatDateForUrl(dueFrom.value);
      if (formattedDueFrom) {
        query.dueFrom = formattedDueFrom;
      }
      const formattedDueTo = formatDateForUrl(dueTo.value);
      if (formattedDueTo) {
        query.dueTo = formattedDueTo;
      }
    }

    if (substatus.value) {
      query.substatus = substatus.value;
      if (substatusMatch.value === "any") {
        query.substatusMatch = "any";
      }
    }

    if (assignedUser.value) {
      query.assignedUser = assignedUser.value;
    }

    if (assignedGroup.value) {
      query.assignedGroup = assignedGroup.value;
    }

    if (sortKey.value) {
      query.sortKey = sortKey.value;
    }

    if (sortDir.value && sortDir.value !== "desc") {
      query.sortDir = sortDir.value;
    }

    if (page.value > 1) {
      query.page = String(page.value);
    }

    return query;
  }

  /**
   * Clear all filters to defaults
   */
  function clearFilters() {
    status.value = DEFAULT_INTAKE_LIST_STATUS;
    sortBy.value = null;
    search.value = "";
    dateFrom.value = null;
    dateTo.value = null;
    dueFrom.value = null;
    dueTo.value = null;
    documentType.value = [];
    modifiedBy.value = [];
    sentBy.value = [];
    tags.value = "";
    tagsMatch.value = "all";
    substatus.value = "";
    substatusMatch.value = "all";
    assignedUser.value = "";
    assignedGroup.value = "";
    sortKey.value = "";
    sortDir.value = "desc";
    page.value = 1;
    showActionNeeded.value = true;
  }

  return {
    // State
    status,
    sortBy,
    search,
    dateFrom,
    dateTo,
    dueFrom,
    dueTo,
    documentType,
    modifiedBy,
    sentBy,
    tags,
    tagsMatch,
    substatus,
    substatusMatch,
    assignedUser,
    assignedGroup,
    sortKey,
    sortDir,
    page,
    showActionNeeded,

    // Computed
    hasTableFilters,

    // Actions
    setFilters,
    getNavigationQuery,
    clearFilters,
  };
});
